import { createStore, type StoreApi } from 'zustand';

import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import type { StatementTriageEntryMode, StatementTriageOpenSource } from '../model/statementTriage';

/**
 * Single-slot handoff store used by the native `fullScreenModal`
 * citation-wizard route prototype.
 *
 * The route screen needs the same heavy inputs the inline Actionsheet
 * variant gets from the bubble's `currentMessage` — extractedStatements,
 * statementCitationStatuses, attachedStatementReferences,
 * statementReferenceHelpRequests. URL params don't carry typed arrays
 * cleanly (and Expo Router serializes them as strings), so the trigger
 * populates this store synchronously right before `router.push`. The
 * route reads `payload` on mount.
 *
 * # Single-slot invariant
 *
 * Only one wizard route can be open at a time. A new handoff call
 * replaces any previous payload — this is intentional for the prototype.
 *
 * # Stale-data guard
 *
 * The trigger (`AddReferencesPromptBubble`) passes `messageSid` as the
 * `expectedMessageSid` URL param when pushing the route. The route
 * validates `handoff.messageSid === expectedMessageSid` on mount and
 * renders `MissingHandoffGuard` (auto-dismiss) if they differ. This
 * guards against the navigation-cancellation edge case: if the user
 * cancels a push mid-flight, the store may hold a payload for a
 * different message when the route eventually mounts.
 *
 * # Lifecycle
 *
 * Cleanup is the route's responsibility: `clearCitationWizardHandoff`
 * is called from the route's unmount effect. If the app is killed
 * mid-navigation, the store starts `null` on relaunch (no persistence)
 * so the route safely renders `MissingHandoffGuard` and auto-dismisses.
 */
export interface CitationWizardHandoffPayload {
  conversationId: string;
  messageSid: string;
  extractedStatements: ExtractedStatement[];
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  attachedStatementReferences: readonly AttachedStatementReference[];
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** How the wizard was opened — `search` (the "Add references" pill)
   * or `provide` (the "I have references in mind" entry). Applied to the
   * triage store on route mount so the selection phase and step
   * indicator reflect the chosen flow. Defaults to `search`. */
  entryMode?: StatementTriageEntryMode;
  /** Where the wizard was opened from — applied to the triage store on route
   * mount so the OPENED analytics event attributes the open. Defaults to
   * `inline_prompt`. */
  openSource?: StatementTriageOpenSource;
}

interface CitationWizardHandoffStore {
  payload: CitationWizardHandoffPayload | null;
  setHandoff: (payload: CitationWizardHandoffPayload) => void;
  clearHandoff: () => void;
}

export const citationWizardHandoffStore = createStore<CitationWizardHandoffStore>((set) => ({
  payload: null,
  setHandoff: (payload) => set({ payload }),
  clearHandoff: () => set({ payload: null }),
}));

export function setCitationWizardHandoff(payload: CitationWizardHandoffPayload): void {
  citationWizardHandoffStore.getState().setHandoff(payload);
}

export function clearCitationWizardHandoff(): void {
  citationWizardHandoffStore.getState().clearHandoff();
}

export type CitationWizardHandoffStoreApi = StoreApi<CitationWizardHandoffStore>;
